/**
 * Ersatzwertbildung für die Standreihe **eines** Zählwerks eines Zählers
 * (HM-115, ADR 0034).
 *
 * **Ersetzt werden Stände, nicht Verbräuche.** Eine Viertelstunde ohne Stand
 * bekommt einen Ersatzstand; der Verbrauch entsteht danach wie immer als
 * Differenz benachbarter Stände (`deltasWithGaps`). Daraus folgt der
 * Mengenabgleich von selbst: Die Verbräuche über die Lücke summieren sich
 * zwingend zur Differenz der beiden gemessenen Stände, denn jeder Ersatzstand
 * liegt zwischen ihnen. Und jeder Verbrauch, an dem ein Ersatzstand beteiligt
 * ist, erbt dessen Status (`worseQuality`) — auch der erste nach der Lücke, der
 * einen gemessenen Stand am Ende hat.
 *
 * **Gefüllt wird nur zwischen zwei gemessenen Ständen.** Wer die Reihe hier
 * hineingibt, gibt die eines einzigen Zählers — über einen Zählerwechsel
 * hinweg gibt es keine Differenz (Regel 4), und ein Rücksprung innerhalb eines
 * Zählers bleibt eine Lücke (`unbridged`, Grund `reset`).
 *
 * Die Wahl des Verfahrens liegt in der Domäne (`chooseSubstitutionMethod`);
 * hier stehen die Rechnung und der Vergleichstag — der braucht die Zeitzone,
 * und Zeitzonen gibt es nur in diesem Paket (Regel 3).
 */
import {
  chooseSubstitutionMethod,
  QUALITY_OF_SUBSTITUTION,
  SUBSTITUTION_COMPARABLE_WEEKS,
  SUBSTITUTION_MAX_GAP_QUARTER_HOURS,
  SUBSTITUTION_SHORT_GAP_QUARTER_HOURS,
  type SubstitutionMethod,
} from "@hausmeter/domain";
import { ENERGY_RESOLUTION, type TimeRange } from "./grid.js";
import type { BucketValue } from "./series.js";
import { localDateOf, sameLocalTimeDaysBefore, shiftLocalDate, startOfLocalDate } from "./timezone.js";

const STEP_MS = ENERGY_RESOLUTION * 60_000;
const DAYS_PER_WEEK = 7;

export interface SubstitutionPolicy {
  readonly shortGapQuarterHours: number;
  readonly maxGapQuarterHours: number;
  readonly comparableWeeks: number;
}

export const DEFAULT_SUBSTITUTION_POLICY: SubstitutionPolicy = {
  shortGapQuarterHours: SUBSTITUTION_SHORT_GAP_QUARTER_HOURS,
  maxGapQuarterHours: SUBSTITUTION_MAX_GAP_QUARTER_HOURS,
  comparableWeeks: SUBSTITUTION_COMPARABLE_WEEKS,
};

/** Eine Lücke zwischen zwei gemessenen Ständen, die gefüllt werden darf. */
export interface BridgeableGap {
  /** Der letzte gemessene Stand vor der Lücke. */
  readonly before: BucketValue;
  /** Der erste gemessene Stand danach. */
  readonly after: BucketValue;
  /** Viertelstunden ohne gemessenen Verbrauch — einschließlich der von `after`. */
  readonly quarterHours: number;
  /** Die Differenz der beiden Stände: die Menge, die verteilt wird. */
  readonly energyWh: number;
}

export interface SubstitutedGap extends BridgeableGap {
  readonly method: SubstitutionMethod;
}

/** Eine Lücke, die eine Lücke bleibt — und warum. */
export interface UnbridgedGap {
  /** Der erste Bucket ohne gemessenen Verbrauch. */
  readonly from: Date;
  /** Der Bucket des Stands danach — sein Verbrauch fehlt ebenfalls. */
  readonly to: Date;
  /**
   * `reset`: der Stand danach ist kleiner (Regel 4). `too_long`: länger als
   * erlaubt. `break`: In der Lücke steht ein echter, nicht gemessener Stand
   * (`breaks`) — die Differenz der beiden gemessenen ist dort nicht die Menge.
   */
  readonly reason: "reset" | "too_long" | "break";
}

export interface SubstitutionResult {
  /** Die Ersatzstände, je fehlendem Bucket einer, mit dem Status ihres Verfahrens. */
  readonly readings: readonly BucketValue[];
  readonly gaps: readonly SubstitutedGap[];
  readonly unbridged: readonly UnbridgedGap[];
}

export interface SubstitutionInput {
  /**
   * Die **gemessenen** Stände eines Zählwerks eines Zählers, beliebig sortiert.
   * Sie dürfen weiter zurückreichen als `range` — für den Vergleichstag.
   */
  readonly measured: readonly BucketValue[];
  /** Nur Lücken, deren Verbräuche dieses Fenster berühren, werden gefüllt. */
  readonly range: TimeRange;
  /** Die Zeitzone der Anlage — der Vergleichstag ist ein lokaler Tag. */
  readonly timeZone: string;
  readonly policy?: SubstitutionPolicy;
  /**
   * Buckets mit einem echten Stand, der nicht gemessen ist (HM-191). Ein
   * virtueller Zähler schreibt so den ersten Stand nach einem Quellenwechsel,
   * an dem eine Menge unbekannt blieb: Die Differenz der gemessenen Stände um
   * ihn herum fehlt genau diese Menge, und ein Ersatzwert darüber summierte sich
   * zu einer Zahl, die kein Zähler gezählt hat. Eine Lücke, die einen enthält,
   * bleibt eine Lücke.
   */
  readonly breaks?: readonly Date[];
}

/**
 * Die Lücken zwischen gemessenen Ständen, die das Fenster berühren.
 *
 * Eine Lücke berührt das Fenster, wenn einer ihrer Verbräuche darin liegt —
 * vom ersten fehlenden Bucket bis zum Bucket des Stands danach, dessen
 * Verbrauch ebenfalls fehlt.
 */
export function gapsBetweenReadings(
  measured: readonly BucketValue[],
  range: TimeRange,
  policy: SubstitutionPolicy = DEFAULT_SUBSTITUTION_POLICY,
  breaks: readonly Date[] = [],
): { readonly bridgeable: readonly BridgeableGap[]; readonly unbridged: readonly UnbridgedGap[] } {
  const breakTimes = breaks.map((at) => at.getTime());
  const ordered = [...measured].sort((a, b) => a.bucketStart.getTime() - b.bucketStart.getTime());
  const bridgeable: BridgeableGap[] = [];
  const unbridged: UnbridgedGap[] = [];

  for (let index = 1; index < ordered.length; index += 1) {
    const before = ordered[index - 1]!;
    const after = ordered[index]!;
    const quarterHours = Math.round((after.bucketStart.getTime() - before.bucketStart.getTime()) / STEP_MS);
    if (quarterHours <= 1) continue;

    const from = new Date(before.bucketStart.getTime() + STEP_MS);
    if (from >= range.to || after.bucketStart < range.from) continue;

    if (after.value < before.value) {
      unbridged.push({ from, to: after.bucketStart, reason: "reset" });
    } else if (breakTimes.some((at) => at > before.bucketStart.getTime() && at < after.bucketStart.getTime())) {
      unbridged.push({ from, to: after.bucketStart, reason: "break" });
    } else if (quarterHours > policy.maxGapQuarterHours) {
      unbridged.push({ from, to: after.bucketStart, reason: "too_long" });
    } else {
      bridgeable.push({ before, after, quarterHours, energyWh: after.value - before.value });
    }
  }
  return { bridgeable, unbridged };
}

/**
 * Ab wann gemessene Stände vorliegen müssen, damit jede dieser Lücken ihren
 * Vergleichstag finden kann — `null`, wenn keine einen braucht.
 *
 * Ein Kalendertag Luft vor der frühesten Woche: Der Verbrauch der ersten
 * Viertelstunde eines Vergleichstags braucht den Stand der Viertelstunde davor.
 */
export function comparableHistoryFrom(
  gaps: readonly BridgeableGap[],
  timeZone: string,
  policy: SubstitutionPolicy = DEFAULT_SUBSTITUTION_POLICY,
): Date | null {
  const needing = gaps.filter((gap) => gap.energyWh > 0 && gap.quarterHours > policy.shortGapQuarterHours);
  if (needing.length === 0) return null;
  const earliest = needing.reduce((min, gap) => (gap.before.bucketStart < min ? gap.before.bucketStart : min), needing[0]!.before.bucketStart);
  const date = shiftLocalDate(localDateOf(earliest, timeZone), { days: -(policy.comparableWeeks * DAYS_PER_WEEK + 1) });
  return startOfLocalDate(date, timeZone);
}

/** Füllt die Lücken, die das Fenster berühren. */
export function substituteReadings(input: SubstitutionInput): SubstitutionResult {
  const policy = input.policy ?? DEFAULT_SUBSTITUTION_POLICY;
  const { bridgeable, unbridged } = gapsBetweenReadings(input.measured, input.range, policy, input.breaks);
  const byTime = new Map(input.measured.map((value) => [value.bucketStart.getTime(), value.value]));

  const readings: BucketValue[] = [];
  const gaps: SubstitutedGap[] = [];

  for (const gap of bridgeable) {
    const profile =
      gap.energyWh > 0 && gap.quarterHours > policy.shortGapQuarterHours
        ? comparableDayProfile(gap, byTime, input.timeZone, policy.comparableWeeks)
        : null;
    const method = chooseSubstitutionMethod({
      quarterHours: gap.quarterHours,
      energyWh: gap.energyWh,
      comparableDayAvailable: profile !== null,
    });
    const weights = method === "comparable_day" && profile !== null ? profile : new Array<number>(gap.quarterHours).fill(1);
    readings.push(...distribute(gap, weights, QUALITY_OF_SUBSTITUTION[method]));
    gaps.push({ ...gap, method });
  }

  return { readings, gaps, unbridged };
}

/**
 * Die Form eines Vergleichstags: je Viertelstunde der Lücke der gemessene
 * Verbrauch zur selben lokalen Uhrzeit eine Woche früher — oder zwei, oder
 * drei, bis eine Woche **vollständig** ist und überhaupt Verbrauch hatte.
 *
 * **Eine Woche ganz oder gar nicht.** Eine Form aus zwei Wochen gemischt hätte
 * keinen Tag zum Vorbild, sondern eine Erfindung aus zweien. Und eine Woche
 * ohne Verbrauch in diesen Stunden sagt nichts darüber, *wann* in der Lücke
 * verbraucht wurde — nur, dass es damals nicht war.
 */
function comparableDayProfile(
  gap: BridgeableGap,
  byTime: ReadonlyMap<number, number>,
  timeZone: string,
  weeks: number,
): readonly number[] | null {
  for (let week = 1; week <= weeks; week += 1) {
    const weights: number[] = [];
    for (let index = 1; index <= gap.quarterHours; index += 1) {
      const bucket = new Date(gap.before.bucketStart.getTime() + index * STEP_MS);
      const comparable = sameLocalTimeDaysBefore(bucket, week * DAYS_PER_WEEK, timeZone);
      if (comparable === null) break;
      const end = byTime.get(comparable.getTime());
      const start = byTime.get(comparable.getTime() - STEP_MS);
      if (end === undefined || start === undefined || end < start) break;
      weights.push(end - start);
    }
    if (weights.length === gap.quarterHours && weights.some((weight) => weight > 0)) return weights;
  }
  return null;
}

/**
 * Die Menge nach Gewichten auf die Viertelstunden verteilt, als Stände.
 *
 * Der Stand am Ende der i-ten Viertelstunde ist der Stand davor plus der Anteil
 * der ersten i Gewichte an der Menge. Der letzte wäre genau `after` — der ist
 * gemessen und wird nicht geschrieben.
 */
function distribute(
  gap: BridgeableGap,
  weights: readonly number[],
  quality: BucketValue["quality"],
): readonly BucketValue[] {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const readings: BucketValue[] = [];
  let cumulative = 0;
  for (let index = 1; index < gap.quarterHours; index += 1) {
    cumulative += weights[index - 1] ?? 0;
    readings.push({
      bucketStart: new Date(gap.before.bucketStart.getTime() + index * STEP_MS),
      value: total === 0 ? gap.before.value : gap.before.value + (gap.energyWh * cumulative) / total,
      quality,
    });
  }
  return readings;
}
